import React from 'react';
import { Container, Row, Col, Card, ProgressBar } from 'react-bootstrap';
import { 
  BarChart3, 
  DollarSign, 
  Package, 
  ShoppingCart, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';  
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';  
import './Dashboard.css';

function Dashboard() {
  const stats = [
    {
      title: "Revenue",
      value: "€25,000",
      change: "+12%",
      trend: "up",
      icon: DollarSign
    },
    {
      title: "Profit",
      value: "€8,000",
      change: "+8%",
      trend: "up",
      icon: TrendingUp
    },
    {
      title: "Stock",
      value: "320",
      change: "-5%",
      trend: "down",
      icon: Package
    },
    {
      title: "Orders",
      value: "230",
      change: "+18%",
      trend: "up",
      icon: ShoppingCart
    }
  ];

  const salesData = [
    { month: "Jan", sales: 50, revenue: 5000, profit: 1500 },
    { month: "Feb", sales: 60, revenue: 6000, profit: 2000 },
    { month: "Mar", sales: 70, revenue: 7000, profit: 2500 },
    { month: "Apr", sales: 80, revenue: 8000, profit: 3000 },
    { month: "May", sales: 65, revenue: 6500, profit: 2200 },
    { month: "Jun", sales: 90, revenue: 9000, profit: 3500 }
  ];

  const topProducts = [
    { name: "Product A", sales: 150 },
    { name: "Product B", sales: 120 },
    { name: "Product C", sales: 90 },
    { name: "Product D", sales: 60 }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <Container fluid className="p-4 bg-light min-vh-100">
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h3 mb-0">Dashboard</h1>
          <small className="text-muted">Last updated: {new Date().toLocaleDateString()}</small>
        </div>

        {/* Stats Grid */}
        <Row className="g-4 mb-4">
          {stats.map((stat, index) => (
            <Col key={index} xs={12} md={6} lg={3}>
              <Card className="stat-card h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="icon-container">
                      <stat.icon size={24} />
                    </div>
                    <span className={`d-flex align-items-center ${
                      stat.trend === 'up' ? 'text-success' : 'text-danger'
                    }`}>
                      {stat.change}
                      {stat.trend === 'up' ? 
                        <ArrowUpRight size={16} className="ms-1" /> : 
                        <ArrowDownRight size={16} className="ms-1" />
                      }
                    </span>
                  </div>
                  <div className="text-muted small">{stat.title}</div>
                  <div className="h4 mb-0 mt-1">{stat.value}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts Section */}
        <Row className="g-4 mb-4">
          {/* Sales Chart */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">Sales Overview</h5>
                  <BarChart3 size={20} className="text-muted" />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#0088FE" 
                      strokeWidth={2}
                      name="Revenue"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#00C49F" 
                      strokeWidth={2}
                      name="Profit"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>

          {/* Top Products */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">Top Products</h5>
                  <Package size={20} className="text-muted" />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={topProducts}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="sales"
                    >
                      {topProducts.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Goals Progress */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="card-title mb-0">Goals Progress</h5>
              <TrendingUp size={20} className="text-muted" />
            </div>
            <ProgressBar now={50} className="mb-2" />
            <div className="d-flex justify-content-between text-muted small">
              <span>Current: 50%</span>
              <span>Target: 100%</span>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </Container>
  );
}

export default Dashboard;